import moment from 'moment';
import { merge } from 'lodash';
import React, { PropTypes } from 'react';
import { DatePicker } from 'material-ui';
import CustomTextField from './TextField';
import CalendarIcon from './../svgs/CalendarIcon';
import { isNil } from 'lodash';

const rexMonthYear = /^(0?[1-9]|1?[0-2])\/([0-9]{2}){1,2}$/g;
const rexYear = /^([0-9]{2}){1,2}$/g;

export default class DateInput extends React.Component {
  static propTypes = {
    onBlur : PropTypes.func,
    onChange : PropTypes.func,
    onErrorChange: PropTypes.func,
    dateFormat : PropTypes.oneOf(['Full', 'MonthYear', 'Year']),
    defaultValue : PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    textStyle: PropTypes.object,
    floatingLabelText: PropTypes.string,
    hintText : PropTypes.string,
    errorText : PropTypes.string,
    className : PropTypes.string,
    isDirty: PropTypes.bool,
    autoOk: PropTypes.bool,
    minDate: PropTypes.object,
    validation: PropTypes.object,
    floatingLabelFixed: PropTypes.bool,
    floatingLabelStyle: PropTypes.object,
    floatingLabelFocusStyle: PropTypes.object,
    disabled: PropTypes.bool,
    'data-id': PropTypes.string,
  }

  static defaultProps = {
    dateFormat : 'Full'
  }

  constructor(props, context) {
    super(props, context);

    const dateValue = this.normalizeValue(props.defaultValue);
    const textValue = this.formatDateString(dateValue, props.dateFormat);

    this.state = {
      dateValue,
      textValue,
      isValid: true,
      isFuture: true,
    };
  }

  componentWillMount() {
    if(this.props.onErrorChange && this.lastNumberErrorSent) {
      this.props.onErrorChange(this.lastNumberErrorSent);
    }
  }

	componentWillReceiveProps(nextProps) {
    const dateValue = this.normalizeValue(nextProps.value, nextProps.defaultValue, this.state.dateValue);
    const textValue = this.formatDateString(dateValue, nextProps.dateFormat) || '';
    const newState = {dateValue, textValue};
    this.setState(newState);
	}

  componentWillUnmount() {
    if(!this.state.isValid && this.props.onErrorChange) {
      this.props.onErrorChange(-1);
    }
  }

  onErrorChange = (value) => {
    if (!this.props.onErrorChange) {
      return;
    }

    // We need to make sure we don't send the same value twice.
    if(this.lastNumberErrorSent !== value) {
      this.props.onErrorChange(value);
      this.lastNumberErrorSent = value;
    }

  };

  lastNumberErrorSent = 1;

  handlePickerChange = (event, newDate) => {
    const newState = merge({}, this.state);
    newState.dateValue = newDate;
    newState.textValue = this.formatDateString(newState.dateValue, this.props.dateFormat);
    newState.isValid = true;
    newState.isFuture = true;
    if (this.props.minDate && newState.isValid) {
      newState.isValid = newState.isFuture = moment(newState.dateValue).isAfter(this.props.minDate);
    }

    if (this.props.onChange) {
      this.props.onChange(event, (new Date(newState.dateValue)).toUTCString());
    }

    if (this.props.onBlur) {
      this.props.onBlur(event, (new Date(newState.dateValue)).toUTCString());
    }

    if (newState.isValid && newState.isFuture) {
      this.onErrorChange(-1);
    }

    this.setState(newState);
  };

  handleTextChange = (event) => {
    const newState = merge({}, this.state);
    newState.textValue = event.target.value;
    newState.isValid = this.isValidFormat(newState.textValue);
    newState.dateValue = (newState.isValid) ? this.normalizeValue(newState.textValue) : null;

    // if minDate provided, we need to validate against the minDate.
    newState.isFuture = true;
    if (this.props.minDate && newState.isValid) {
      newState.isValid = newState.isFuture = moment(newState.dateValue).isAfter(this.props.minDate);
      newState.dateValue = newState.isValid ? newState.dateValue : null;
    }

    if (this.state.isValid && !newState.isValid) {
      this.onErrorChange(1);
    }
    else if (!this.state.isValid && newState.isValid) {
      this.onErrorChange(-1);
    }

    if (this.props.onChange) {
      this.props.onChange(Object.assign({}, event, {target: {value: newState.dateValue}}));
    }
    this.setState(newState);
  };

  handleBlur = (event) => {
    if (this.props.onBlur) {
      this.props.onBlur(Object.assign({}, event, {target: {value: this.state.dateValue}}), this.state.dateValue);
    }
  };

  handleTogglePicker = () => {

    if(this.props.disabled) {
      return;
    }

    this.refs.datePicker.openDialog();
  }

  isValidFormat(stringDate) {
    return (this.props.dateFormat === 'Year') ? !!stringDate.match(rexYear) :
            (this.props.dateFormat === 'MonthYear') ? !!stringDate.match(rexMonthYear) :
             (moment(stringDate, ['MM/DD/YYYY', 'MM/DD/YY', 'MM/D/YYYY', 'M/DD/YYYY', 'M/D/YYYY', 'M/D/YY'], true).isValid());
  }

  formatDateString(value, format) {
    if (!moment(value).isValid()) {
      return undefined;
    }

    switch (format) {
      case 'Year':
        return moment(value).format('YYYY');

      case 'MonthYear':
        return moment(value).format('MM/YYYY');

      default:
        return moment(value).format('L');

    }
  }

  normalizeValue(value, ...nextValues) {

    // tricksy hobbits: if multiple values passed, normalize the first populated one...
    if (isNil(value) && nextValues.length > 1) {
      return this.normalizeValue(...nextValues);
    }

    if (typeof value === 'string') {
      if (value.match(rexMonthYear)) {
        const [month, year] = value.split('/');
        return new Date(year, parseInt(month, 10) - 1); // Jan = 0, Dec = 11
      }
      else if (value.match(rexYear)) {
        return new Date(value, 0); // Jan = 0
      }
      else if (moment(value).isValid()) {
        return new Date(value);
      }
    }
    else if (value instanceof Date) {
      return value;
    }

    return null;
  }

  render() {
    const {
      className,
      errorText,
      autoOk,
      floatingLabelText,
      hintText,
      isDirty,
      minDate,
      validation,
      floatingLabelFixed,
      textStyle,
      'data-id': dataId,
      floatingLabelStyle,
      floatingLabelFocusStyle,
    } = this.props;

    const isEmptyString = (val) => (typeof val === 'string' && val.length > 0);
    let invalidDateErrorMessage = (isEmptyString(this.state.textValue) && !this.state.isValid) ? 'Invalid date format.' : undefined;

    // if minDate provided, we need to validate against the minDate.
    if (this.props.minDate && !this.state.isFuture) {
      invalidDateErrorMessage = 'Please enter a future date.';
    }

    const defaultClassName = className || 'date-input';

    return (
      <div className={`${defaultClassName}-container`}>
        <CustomTextField
          style={textStyle}
          data-id={`${dataId}-textfield`}
          className={`${defaultClassName}-mask`}
          floatingLabelText={floatingLabelText}
          hintText={hintText}
          onChange={this.handleTextChange}
          onBlur={this.handleBlur}
          errorText={invalidDateErrorMessage || errorText}
          value={this.state.textValue}
          isDirty={isDirty}
          floatingLabelFixed={floatingLabelFixed}
          floatingLabelFocusStyle={floatingLabelFocusStyle}
          validation={validation}
          disabled={this.props.disabled}
          floatingLabelStyle={floatingLabelStyle}
        />
        { !this.props.disabled &&
          <div data-id={`${dataId}-button`} className={`${defaultClassName}-icon`}>
            <CalendarIcon
              disabled={this.props.disabled}
              onClick={this.handleTogglePicker}
            />
          </div>
        }
        <DatePicker
          disabled={this.props.disabled}
          data-id={`${dataId}-datepicker`}
          ref='datePicker'
          className={`${defaultClassName}-picker`}
          container={'inline'}
          autoOk={autoOk}
          onChange={this.handlePickerChange}
          onDismiss={this.handleBlur}
          textFieldStyle={{display: 'none'}}
          value={this.state.dateValue}
          minDate={minDate}
        />

      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./shared/common/components/DateInput.js